import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { ReEvent, Id } from "../interfaces/types";
import {
  addOrUpdateEventInFirebase,
  fetchAllEvents,
} from "../services/firebaseService";

// Define the type for the context, including optional events array and functions to add and update events
export type EventContextType = {
  events?: ReEvent[];
  upcomingEvents?: ReEvent[];
  addEvent: (newEvent: ReEvent) => void;
  updateEvent: (updatedEvent: ReEvent) => void;
  getEventBySessionId: (sessionId: Id) => ReEvent | undefined;
  getEventById: (eventId: Id) => ReEvent | undefined;
  isXpFactorActive: (event: ReEvent) => boolean;
};

// Create the context with default values
const EventContext = createContext<EventContextType>({
  events: [],

  upcomingEvents: [],
  addEvent: () => { },
  updateEvent: () => { },
  getEventBySessionId: () => undefined,
  getEventById: () => undefined,
  isXpFactorActive: () => false,
});

// Define the EventProvider component which will wrap the children components and provide the context
export const EventProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [events, setEvents] = useState<ReEvent[]>([]);

  // Fetch all events from Firebase when the component mounts
  useEffect(() => {
    fetchAllEvents().then((fetchedEvents) => {
      setEvents(fetchedEvents);
    });
  }, []); // Empty dependency array ensures this runs only once on mount

  // Function to add a new event
  function addEvent(newEvent: ReEvent) {
    addOrUpdateEventInFirebase(newEvent);
    setEvents([...events, newEvent]);
  }

  // Function to update an existing event
  function updateEvent(updatedEvent: ReEvent) {
    addOrUpdateEventInFirebase(updatedEvent);

    // Update the local state with the updated event
    const updatedEvents = events?.map((event) => {
      if (event.id === updatedEvent.id) {
        return updatedEvent;
      }
      return event;
    });
    setEvents(updatedEvents);
  }

  const upcomingEvents = events?.filter(event => {
    return event.startDate && new Date(event.startDate) > new Date();
  });

  const getEventBySessionId = (sessionId: Id) => {
    return events?.find(event => event.sessions?.includes(sessionId));
  }

  const getEventById = (eventId: Id | undefined) => {
    return events?.find(event => event.id === eventId);
  }

  const isXpFactorActive = (event: ReEvent) => {
    return event.eventConfiguration?.useXpFactor || false;
  }


  const value: EventContextType = {
    events,
    upcomingEvents,
    addEvent,
    updateEvent,
    getEventBySessionId,
    getEventById,
    isXpFactorActive,
  };

  return (
    // Provide the context to the children components
    <EventContext.Provider value={value}>{children}</EventContext.Provider>
  );
};

// Custom hook to use the EventContext
export const useEventContext = (): EventContextType => {
  return useContext(EventContext);
};
